import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { formatPhone } from "@/lib/format";
import { BUSINESS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please add your name, phone, and a short message.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email.trim()) delete payload.email;
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Thank you! Nicole will reach out to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call Nicole directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-sand-deep">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        {/* Invitation */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="eyebrow mb-4">Let's Talk</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-charcoal tracking-tight leading-tight">
            Ready to talk about care for your loved one?
          </h2>
          <p className="mt-6 text-lg text-charcoal-soft leading-relaxed max-w-md">
            Nicole would be glad to answer your questions — no pressure, just an
            honest conversation.
          </p>

          <div className="mt-10 space-y-5">
            <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-4 group">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage/15 text-sage-700 group-hover:bg-sage group-hover:text-white transition-colors">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs text-charcoal-soft">Call directly</span>
                <span className="block text-lg text-charcoal">{BUSINESS.phoneDisplay}</span>
              </span>
            </a>
            <a href={BUSINESS.emailHref} data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage/15 text-sage-700 group-hover:bg-sage group-hover:text-white transition-colors">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs text-charcoal-soft">Email</span>
                <span className="block text-lg text-charcoal">{BUSINESS.email}</span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage/15 text-sage-700">
                <MapPin className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs text-charcoal-soft">Service area</span>
                <span className="block text-lg text-charcoal">{BUSINESS.area}</span>
              </span>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={submit}
          data-testid="inquiry-form"
          className="rounded-[2rem] border border-charcoal/10 bg-white/70 backdrop-blur-md p-7 sm:p-10 soft-shadow-lg"
        >
          <h3 className="font-serif text-2xl text-charcoal mb-6">Request a call from Nicole</h3>
          <div className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-charcoal-soft">Your name</Label>
              <Input
                id="name"
                data-testid="inquiry-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Doe"
                className="mt-2 bg-white/70 border-charcoal/10 rounded-xl h-12"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="phone" className="text-charcoal-soft">Phone</Label>
                <Input
                  id="phone"
                  data-testid="inquiry-phone"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                  placeholder="(970) 555-1234"
                  className="mt-2 bg-white/70 border-charcoal/10 rounded-xl h-12"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-charcoal-soft">Email <span className="text-charcoal/40">(optional)</span></Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="inquiry-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="mt-2 bg-white/70 border-charcoal/10 rounded-xl h-12"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="message" className="text-charcoal-soft">How can Nicole help?</Label>
              <Textarea
                id="message"
                data-testid="inquiry-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell Nicole a little about your loved one and the care you're looking for…"
                rows={4}
                className="mt-2 bg-white/70 border-charcoal/10 rounded-xl resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="inquiry-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage text-white py-4 font-medium hover:bg-sage-dark transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Sending…" : "Send to Nicole"}
            </button>
            <p className="text-center text-xs text-charcoal-soft">
              Prefer to talk now? Call {BUSINESS.phoneDisplay}.
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
